import type { InputHTMLAttributes } from "react";
import React, { useContext, useLayoutEffect, useMemo } from "react";
import type { DeepMap, FieldError } from "react-hook-form";
import styled, { ThemeContext } from "styled-components/macro";
import { showErrors } from "../../util/util-components";
import { useTranslation } from "react-i18next";
import { SendIcon } from "../Icons/Icons";

interface ITextAreaProps extends InputHTMLAttributes<HTMLTextAreaElement> {
  identifier?: string;
  Icon?: any; // can be string or SVG component
  isValid?: any;
  label: string;
  name: string;
  theref?: any;
  type?: any;
  required: boolean;
  style?: React.CSSProperties;
  wrapperStyle?: React.CSSProperties;
  rows?: number;
  autoHeight?: boolean;
  autoGrow?: boolean;
  minRows?: number;
  maxRows?: number;
  formState?: any;
  validate?: any;
  defaultValue?: string;
  disabled?: boolean;
  tabIndex?: number;
  errors?: DeepMap<Record<string, any>, FieldError>;
  onSend?: () => void;
  sendDisabled?: boolean;
  sendLoading?: boolean;
  sendIcon?: React.ReactNode;
  hideFloatingLabel?: boolean;
  placeholderText?: string;
}

interface IInputProps {
  invalid?: any;
  theme?: any;
}

const Input = styled.textarea.attrs({})<{ $autoGrow?: boolean }>`
  box-sizing: border-box;
  width: 100%;
  height: ${({ $autoGrow }) => ($autoGrow ? "auto" : "100%")};
  overflow: ${({ $autoGrow }) => ($autoGrow ? "hidden" : "auto")};
  border: none;
  outline: none;
  resize: ${({ $autoGrow }) => ($autoGrow ? "vertical" : "none")};
  background-color: ${({ disabled, theme }) =>
    disabled ? theme.disabledInput : theme.primaryBG};
  font-size: ${({ theme }) => theme.fontSizes.regular};
  &::placeholder {
    color: ${({ theme }) => theme.secondaryTextColor};
    opacity: 0.9;
  }
`;

const FloatingLabel = styled.span<IInputProps>`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  color: ${(props: IInputProps) =>
    props.invalid ? props.theme.errorColor : props.theme.secondaryTextColor};

  position: absolute;
  pointer-events: none;
  left: 13px;
  top: 14px;
  transition: 0.2s ease all;
`;

type WrapperProps = {
  extraSmall?: boolean;
  small?: boolean;
  disabled?: boolean;
  invalid?: any;
  theme?: any;
  autoHeight?: boolean;
  autoGrow?: boolean;
};
const Wrapper = styled.div<WrapperProps>`
  position: relative;
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  height: ${({ autoHeight, autoGrow }) =>
    autoHeight || autoGrow ? "fit-content" : "120px"};
  min-height: ${({ autoHeight, autoGrow }) =>
    autoHeight || autoGrow ? "0" : "unset"};
  background-color: ${({ theme, disabled }) =>
    disabled ? theme.disabledInput : theme.primaryBG};
  padding: ${({ theme, autoGrow }) =>
    autoGrow
      ? `${theme.resolve("spacing_lg")} ${theme.resolve("spacing_sm")}`
      : `21px 13px ${theme.resolve("spacing_sm")} ${theme.resolve(
          "spacing_lg"
        )}`};
  font-size: ${({ theme }) => theme.fontSizes.regular};
  resize: ${({ autoHeight, autoGrow }) =>
    autoHeight || autoGrow ? "none" : "vertical"};
  overflow: ${({ invalid, autoGrow }) =>
    invalid || autoGrow ? "visible" : "hidden"};
  border: ${({ invalid, theme }: IInputProps) =>
    (invalid && `${theme.resolve("border_2xl")} solid ${theme.errorColor}`) ||
    `${theme.resolve("border_md")} solid ${theme.primaryBorder}`};
  /* prettier-ignore */
  ${Input}:focus + ${FloatingLabel},
  ${Input}:not(:placeholder-shown) + ${FloatingLabel},
  ${Input}:read-only + ${FloatingLabel} & {
    outline: none;
    top: 7px;
    bottom: 10px;
    left: 13px;
    font-size: ${({ theme }) => theme.fontSizes.xs};
    opacity: 1;
    max-width: ${(props) =>
    props.small ? "176px" : props.extraSmall ? "78px" : "unset"};
  }
  &:focus-within {
    border: ${({ invalid, theme }: IInputProps) =>
      (invalid && `${theme.resolve("border_2xl")} solid ${theme.errorColor}`) ||
      `${theme.resolve("border_2xl")} solid ${theme.tertiaryBorder}`};
  }
`;

const TextAreaWrapper = styled.div<{
  autoHeight?: boolean;
  autoGrow?: boolean;
}>`
  position: relative;
  width: 100%;
  ${({ autoHeight, autoGrow }) =>
    (autoHeight || autoGrow) &&
    `
    height: fit-content;
    min-height: 0;
  `}
`;

const IdentifierWrapper = styled.div<IInputProps>`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  position: absolute;
  top: 15px;
  right: 14px;
`;

interface SendButtonProps {
  disabled?: boolean;
}

const SendButton = styled.button<SendButtonProps>`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: ${({ theme, disabled }) =>
    disabled ? theme.disabledInput : theme.brandColor};
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  svg {
    fill: ${({ theme }) => theme.primaryButtonTextColor};
    width: 16px;
    height: 16px;
  }
`;

export const TextArea = ({
  identifier,
  Icon,
  label,
  name,
  theref,
  required,
  formState,
  style,
  wrapperStyle,
  autoHeight,
  autoGrow,
  minRows,
  maxRows,
  rows,
  validate,
  defaultValue,
  disabled,
  tabIndex,
  errors,
  onChange,
  value,
  onSend,
  sendDisabled,
  sendLoading,
  sendIcon,
  onKeyDown,
  hideFloatingLabel,
  placeholderText,
}: ITextAreaProps) => {
  const { touched } = formState;
  const theme = useContext(ThemeContext);
  const { t } = useTranslation();
  if (touched[name]) {
    if (validate) {
      validate(name);
    }
  }

  const minR = minRows ?? 3;
  const maxR = maxRows ?? 15;

  const combinedStyle = useMemo(() => {
    if (!autoGrow) return style;
    if (!style) return { width: "100%" };
    const {
      height: _h,
      minHeight: _mh,
      maxHeight: _xh,
      ...rest
    } = style as React.CSSProperties;
    return { width: "100%", ...rest };
  }, [autoGrow, style]);

  useLayoutEffect(() => {
    if (!autoGrow || !theref?.current) return;
    const el = theref.current;
    const cs = getComputedStyle(el);
    const lh = parseFloat(cs.lineHeight);
    const lineHeight =
      Number.isFinite(lh) && lh > 0
        ? lh
        : (parseFloat(cs.fontSize) || 14) * 1.25;
    const minPx = Math.ceil(minR * lineHeight);
    const maxPx = Math.ceil(maxR * lineHeight);

    el.style.minHeight = `${minPx}px`;
    el.style.maxHeight = `${maxPx}px`;

    el.style.height = "auto";
    const sh = el.scrollHeight;
    const next = Math.min(maxPx, Math.max(minPx, sh));
    el.style.height = `${next}px`;
    el.style.overflowY = sh > maxPx + 1 ? "auto" : "hidden";
  }, [autoGrow, minR, maxR, value, defaultValue, theref]);

  return (
    <TextAreaWrapper autoHeight={autoHeight} autoGrow={autoGrow}>
      <Wrapper
        disabled={disabled}
        invalid={errors && errors[name]?.type}
        autoHeight={autoHeight}
        autoGrow={autoGrow}
        style={wrapperStyle}
      >
        <Input
          placeholder={placeholderText ?? " "}
          required={required}
          ref={theref}
          name={name}
          defaultValue={defaultValue}
          disabled={disabled}
          tabIndex={tabIndex}
          onChange={onChange}
          onKeyDown={onKeyDown}
          value={value}
          style={combinedStyle}
          $autoGrow={autoGrow}
          rows={rows ?? (autoGrow ? minR : undefined)}
        />
        {!hideFloatingLabel && (
          <FloatingLabel invalid={errors && errors[name]?.type}>
            {label}
          </FloatingLabel>
        )}
        <IdentifierWrapper invalid={errors && errors[name]?.type}>
          {identifier || ""}
        </IdentifierWrapper>
        <IdentifierWrapper>
          {Icon ? (
            <Icon
              fill={
                errors && errors[name]?.type
                  ? theme.resolve("color_icon_error")
                  : theme.resolve("color_icon_primary")
              }
            />
          ) : null}
        </IdentifierWrapper>
        {onSend && (
          <SendButton
            type="button"
            onClick={onSend}
            disabled={sendDisabled || sendLoading}
          >
            {sendIcon ?? <SendIcon />}
          </SendButton>
        )}
      </Wrapper>
      {errors && showErrors({ errors, name, t })}
    </TextAreaWrapper>
  );
};
